/* SEARCH-TAG: page-template  */
import React, { useState, useLayoutEffect, useContext } from 'react';
import { graphql } from 'gatsby';
import { shape, arrayOf } from 'prop-types';
import styled from 'styled-components';
import { getImage } from 'gatsby-plugin-image';
import blogArticleProps from 'TMPropTypes/blogArticleProps';
import { breakpoints } from 'TMConstants';
import HeadTags from 'TMComponents/HeadTags';
import Main from 'TMComponents/Layout/Main';
import Input from 'TMComponents/form/Input';
import { Button } from 'TMComponents/Button';
import Canonical from 'TMComponents/Canonical';
import { BreadcrumbsContext } from 'TMComponents/Breadcrumbs';
import { BannerContext } from 'TMComponents/Banner';
import { LayoutContext } from 'TMComponents/Layout';
import BlogCard from 'TMComponents/BlogCard';
import Content from 'TMComponents/Content';
import { spacing } from '../../../designSystem/spacing';

const PageContainer = styled.div`
    max-width: 1120px;
    margin: 0 auto;
    padding: 0 ${spacing.medium};

    @media (min-width: ${breakpoints.mobileTablet}px) {
        padding: 0 ${spacing.large};
    }
`;

const HeaderContent = styled.div`
    margin: ${spacing.small} 0 ${spacing.large} 0;
`;

const SectionTitle = styled.h2`
    font-size: ${spacing.mediumLarge};
    font-weight: 600;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin: ${spacing.xxLarge} 0 ${spacing.mediumLarge} 0;
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${spacing.mediumLarge};
    margin: ${spacing.large} 0;

    @media (min-width: ${breakpoints.mobileTablet}px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: ${breakpoints.tabletDesktop}px) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const LoadMoreWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin: ${spacing.xxLarge} 0;
`;

const FollowSection = styled.div`
    margin: ${spacing.xxxLarge} 0 ${spacing.xxLarge} 0;
    text-align: center;
`;

const NewsPage = ({ data }) => {
    const [filter, setFilter] = useState('');
    const [pageNo, setPageNo] = useState(1);

    const articles = data.allArticles.nodes.filter(
        ({ title, summary }) => {
            const lcFilter = filter.toLowerCase();
            return (
                title.toLowerCase().includes(lcFilter) ||
                summary.toLowerCase().includes(lcFilter)
            );
        }
    );

    const isBannerAllowed = true;
    const isCrumbsAllowed = true;

    const { setIsNavHidden } = useContext(LayoutContext);
    const { setShowBanner } = useContext(BannerContext);
    const { setCrumbs } = useContext(BreadcrumbsContext);

    useLayoutEffect(() => {
        setIsNavHidden(false);
        setShowBanner(isBannerAllowed);
        setCrumbs({
            show: isCrumbsAllowed,
            crumbList: [{ title: 'News', link: '/blog/news/' }]
        });
    }, []);

    return (
        <Main
            isBannerAllowed={isBannerAllowed}
            isCrumbsAllowed={isCrumbsAllowed}
            reducedCrumbSpacing>
            <HeadTags tags={data.pageContent.seoMetaTags.tags} />
            <Canonical href="blog/news" />
            <PageContainer>
                {data.pageContent.heroContent && (
                    <HeaderContent>
                        <Content
                            content={data.pageContent.heroContent}
                            leftAlign
                            noSidePadding
                        />
                    </HeaderContent>
                )}

                <Input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder={data.pageContent.searchPlaceholder}
                    search
                />

                <SectionTitle>
                    {filter
                        ? 'Search results'
                        : data.pageContent.latestArticleText}
                </SectionTitle>

                <CardsGrid>
                    {(filter
                        ? articles
                        : articles.slice(0, 6 * pageNo)
                    ).map((article) => (
                        <BlogCard
                            key={article.id}
                            title={article.title}
                            slug={article.slug}
                            image={getImage(article.featuredImage)}
                            excerpt={article.summary}
                            tags={article.categories.map(
                                (cat) => cat.name
                            )}
                        />
                    ))}
                </CardsGrid>

                {!filter && 6 * pageNo < articles.length && (
                    <LoadMoreWrapper>
                        <Button onClick={() => setPageNo(pageNo + 1)}>
                            Load more articles
                        </Button>
                    </LoadMoreWrapper>
                )}

                <FollowSection>
                    {/* Follow us social section - to be implemented */}
                </FollowSection>
            </PageContainer>
        </Main>
    );
};

export const pageQuery = graphql`
    {
        pageContent: datoCmsNewsLandingPage {
            seoMetaTags {
                ...GatsbyDatoCmsSeoMetaTags
            }
            heroContent {
                ...textStructuredFields
            }
            headerImageDesktop {
                gatsbyImageData(
                    width: 768
                    layout: CONSTRAINED
                    imgixParams: { auto: "compress" }
                )
            }
            headerImageMobile {
                gatsbyImageData(
                    width: 768
                    layout: CONSTRAINED
                    imgixParams: { auto: "compress" }
                )
            }
            latestArticleText
            searchPlaceholder
            frameVariant
        }
        allArticles: allDatoCmsBlogArticle(
            filter: {
                categories: {
                    elemMatch: { name: { eq: "thinkmoney News" } }
                }
            }
            limit: 18
            sort: { date: DESC }
        ) {
            nodes {
                id
                title
                slug
                summary
                featuredImage {
                    gatsbyImageData(
                        width: 400
                        layout: CONSTRAINED
                        placeholder: NONE
                    )
                }
                categories {
                    name
                }
            }
        }
    }
`;

NewsPage.propTypes = {
    data: shape({
        allArticles: shape({
            nodes: arrayOf(shape(blogArticleProps))
        })
    }).isRequired
};

export default NewsPage;
